import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, Leaf, Shield, Brain, Heart, Moon, Zap, ChevronRight, Star, User, LogIn, Mail, Sparkles, Loader2, CheckCircle2, AlertTriangle, Pill, FileText, Lock, X, MessageSquare, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { toast } from 'sonner';
import TestimonialModal from '../components/TestimonialModal';
import AppStoreBadges from '../components/AppStoreBadges';

// Hero images for rotation - diverse, multi-ethnic, active individuals
// Using smaller image sizes for faster loading
const heroImages = [
  {
    src: "https://images.unsplash.com/photo-1673969695494-ed949400e529?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=800",
    alt: "Elderly couple cycling together on a sunny street"
  },
  {
    src: "https://images.unsplash.com/photo-1537735319956-df7db4b6a4e9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=800",
    alt: "Happy senior couple embracing and smiling"
  },
  {
    src: "https://images.pexels.com/photos/8498170/pexels-photo-8498170.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=500&w=800",
    alt: "Active senior woman celebrating health"
  },
  {
    src: "https://images.unsplash.com/photo-1683194381604-b0f7ee52584a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=800",
    alt: "Diverse group of people running together"
  },
  {
    src: "https://images.unsplash.com/photo-1683194613814-6929a919c1fb?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=800",
    alt: "Multi-ethnic friends jogging outdoors"
  }
];

const categories = [
  { icon: Moon, name: 'Sleep', color: 'bg-indigo-100 text-indigo-600' },
  { icon: Zap, name: 'Energy', color: 'bg-amber-100 text-amber-600' },
  { icon: Shield, name: 'Immunity', color: 'bg-green-100 text-green-600' },
  { icon: Brain, name: 'Focus', color: 'bg-purple-100 text-purple-600' },
  { icon: Heart, name: 'Heart', color: 'bg-rose-100 text-rose-600' },
  { icon: Leaf, name: 'Wellness', color: 'bg-teal-100 text-teal-600' },
];

const features = [
  {
    title: 'Personalized Assessment',
    description: 'Answer a quick health quiz and get recommendations tailored to your unique needs.',
    icon: '01'
  },
  {
    title: 'AI-Powered Insights',
    description: 'Our AI analyzes your responses and medications to ensure safe, effective recommendations.',
    icon: '02'
  },
  {
    title: 'Complete Supplement Protocol',
    description: 'Get dosage, timing, and how-to-take instructions for each recommended supplement.',
    icon: '03'
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  // Rotate hero images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch approved testimonials
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setTestimonials(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
      }
    };
    fetchTestimonials();
  }, []);

  const handleStartQuiz = () => {
    navigate('/quiz');
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setSubscribing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubscribed(true);
        toast.success(data.message);
      } else {
        toast.error(data.detail || 'Failed to subscribe');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Logo size="small" linkTo="/" />
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/quiz" className="text-sm font-medium text-muted-foreground hover:text-[#1A1A1A] transition-colors" data-testid="nav-assessment">Assessment</Link>
            <Link to="/supplements" className="text-sm font-medium text-muted-foreground hover:text-[#1A1A1A] transition-colors" data-testid="nav-supplements">Supplement Guide</Link>
            <Link to="/about" className="text-sm font-medium text-muted-foreground hover:text-[#1A1A1A] transition-colors" data-testid="nav-about">About</Link>
            <Link to="/community" className="text-sm font-medium text-muted-foreground hover:text-[#1A1A1A] transition-colors" data-testid="nav-blog">Blog</Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="btn-primary rounded-full px-4 sm:px-6 text-sm sm:text-base"
                data-testid="header-dashboard-btn"
              >
                <User className="w-4 h-4 mr-1 sm:mr-2" />
                Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/auth?mode=login')}
                  className="rounded-full px-3 sm:px-6 text-sm sm:text-base hidden sm:inline-flex"
                  data-testid="header-signin-btn"
                >
                  <LogIn className="w-4 h-4 mr-1 hidden sm:inline" />
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/auth?mode=register')}
                  className="btn-primary rounded-full px-4 sm:px-6 text-sm sm:text-base hidden sm:inline-flex"
                  data-testid="header-signup-btn"
                >
                  Sign Up
                </Button>
              </>
            )}
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden border-t border-border/50 bg-white"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col px-4 py-3 gap-1">
                <Link to="/quiz" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#1A1A1A] hover:bg-gray-50 transition-colors">
                  <Pill className="w-4 h-4 text-primary" /> Assessment
                </Link>
                <Link to="/supplements" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#1A1A1A] hover:bg-gray-50 transition-colors">
                  <Leaf className="w-4 h-4 text-primary" /> Supplement Guide
                </Link>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#1A1A1A] hover:bg-gray-50 transition-colors">
                  <Shield className="w-4 h-4 text-primary" /> About
                </Link>
                <Link to="/community" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#1A1A1A] hover:bg-gray-50 transition-colors">
                  <MessageSquare className="w-4 h-4 text-primary" /> Blog
                </Link>
                {!user && (
                  <div className="flex gap-2 mt-2 pt-2 border-t border-border/50">
                    <Button
                      variant="outline"
                      onClick={() => { setMobileMenuOpen(false); navigate('/auth?mode=login'); }}
                      className="flex-1 rounded-full text-sm"
                    >
                      Sign In
                    </Button>
                    <Button
                      onClick={() => { setMobileMenuOpen(false); navigate('/auth?mode=register'); }}
                      className="flex-1 btn-primary rounded-full text-sm"
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-10">
        <div className="gradient-hero absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-6 pt-10 pb-12 md:pt-16 md:pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Leaf className="w-4 h-4" />
                Personalized Nutrition
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
                Smart Supplements.
                <br />
                <span className="text-primary">Zero Guesswork.</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                Take our comprehensive health assessment and discover the perfect supplements for your unique needs. Powered by AI with medication interaction safety checks.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleStartQuiz}
                  data-testid="start-quiz-button"
                  className="btn-primary flex items-center gap-2 text-base"
                >
                  Take Free Assessment
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/supplements')}
                  className="btn-secondary text-base"
                >
                  Explore Supplement Guides
                </Button>
              </div>
              <div className="mt-6">
                <p className="text-xs font-medium text-muted-foreground mb-3" data-testid="hero-download-label">
                  Or download the app
                </p>
                <AppStoreBadges variant="both" size="md" align="start" />
              </div>
              <p className="text-xs text-muted-foreground/70 mt-4">
                Vitala is 100% independent. Evidence-based guidance you can trust.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentImageIndex}
                    src={heroImages[currentImageIndex].src}
                    alt={heroImages[currentImageIndex].alt}
                    className="w-full h-[500px] object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-white w-6' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                      aria-label={`View image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
              {/* Floating cards */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute -left-8 top-20 glass p-4 rounded-xl shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Immunity</p>
                    <p className="text-xs text-muted-foreground">Score: 85%</p>
                  </div>
                </div>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
                className="absolute -right-4 bottom-24 glass p-4 rounded-xl shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Energy</p>
                    <p className="text-xs text-muted-foreground">+40% improved</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section - Compact */}
      <section className="py-6 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-2">
              Discover Your Health Needs
            </h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Explore supplements across all major health categories
            </p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                className="group cursor-pointer"
                onClick={() => navigate('/supplements')}
              >
                <div className="p-3 rounded-lg bg-[#F5F5F0] border border-transparent hover:border-primary/20 transition-all duration-300 text-center">
                  <div className={`w-9 h-9 mx-auto rounded-full ${cat.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                    <cat.icon className="w-4 h-4" />
                  </div>
                  <p className="text-sm font-medium text-[#1A1A1A]">{cat.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Vitala? Section */}
      <section className="py-10 bg-[#FAFAF9] border-t border-border/30">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-sm uppercase tracking-widest text-primary font-medium">Why Vitala?</span>
            <h2 className="text-2xl md:text-4xl font-semibold text-[#1A1A1A] mt-3 mb-4">
              We're Not Here to Sell You Supplements
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              In a $50 billion industry full of hype and hidden agendas, Vitala offers something different: 
              unbiased, clinically-informed guidance with no strings attached.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm"
            >
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No Affiliate Links</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We don't earn commissions on what you buy. Our recommendations are based purely on what's 
                right for your health—not our bottom line.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm"
            >
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No Brand Sponsorships</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We don't accept money from supplement companies. Period. No sponsored content, 
                no "preferred partners," no hidden deals.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm"
            >
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">Clinical AI, Not Marketing AI</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our AI is trained to analyze your health profile and cross-reference medications—not 
                to upsell you on trendy ingredients.
              </p>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <Link 
              to="/about" 
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Learn more about our mission
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Medication Interaction Hero Section */}
      <section className="py-10 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-72 h-72 bg-amber-200 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-200 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-6">
                <AlertTriangle className="w-4 h-4" />
                Safety First Feature
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-[#1A1A1A] mb-6 leading-tight">
                Taking Medications?<br />
                <span className="text-amber-600">We Check for Interactions.</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Many people avoid supplements because they fear dangerous interactions with their prescriptions. 
                Our AI-powered system cross-references your medications with every supplement recommendation, 
                alerting you to potential conflicts <strong>before</strong> you make a purchase.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A]">Real-Time Drug Interaction Analysis</h4>
                    <p className="text-muted-foreground text-sm">Checks against thousands of known medication-supplement interactions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A]">Clear Safety Warnings</h4>
                    <p className="text-muted-foreground text-sm">Get clear alerts with explanations you can share with your doctor</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A]">Safe Alternatives Suggested</h4>
                    <p className="text-muted-foreground text-sm">When interactions exist, we suggest safer supplement options</p>
                  </div>
                </div>
              </div>
              <Button 
                onClick={handleStartQuiz}
                size="lg"
                className="btn-primary text-lg px-8 py-6"
              >
                Check Your Medications Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Interaction Check Preview Card */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-border/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Pill className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A]">Interaction Analysis</h3>
                    <p className="text-sm text-muted-foreground">Sample Report Preview</p>
                  </div>
                </div>
                
                {/* Sample Interaction Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Moderate Interaction Detected</p>
                      <p className="text-sm text-amber-700 mt-1">
                        <strong>Metformin + Vitamin B12:</strong> Long-term metformin use may reduce B12 absorption. 
                        Consider B12 supplementation with regular monitoring.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Safe Supplement */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">No Interactions Found</p>
                      <p className="text-sm text-green-700 mt-1">
                        <strong>Vitamin D3:</strong> Safe to take with your current medications. 
                        Recommended dose: 2000-4000 IU daily.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">5,000+</p>
                    <p className="text-xs text-muted-foreground">Interactions Checked</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">100%</p>
                    <p className="text-xs text-muted-foreground">Free Analysis</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-primary">AI</p>
                    <p className="text-xs text-muted-foreground">Powered</p>
                  </div>
                </div>
              </div>
              
              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
                className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
              >
                Most Requested Feature
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sample Report Preview Section */}
      <section className="py-12 bg-[#1A1A1A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-secondary rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-sm uppercase tracking-widest text-primary font-medium">See Before You Commit</span>
              <h2 className="text-3xl md:text-5xl font-bold mt-4 mb-6">
                Preview Your Personalized Report
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
                Wondering what insights you'll get? Take a sneak peek at a sample AI-powered assessment report. 
                No sign-up required to see the quality of analysis you'll receive.
              </p>
              <Button 
                onClick={() => setShowSampleReport(true)}
                size="lg"
                className="bg-white text-[#1A1A1A] hover:bg-gray-100 text-lg px-8 py-6"
              >
                <FileText className="mr-2 w-5 h-5" />
                View Sample Report
              </Button>
            </motion.div>
          </div>
          
          {/* Preview Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
            >
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Health Scores</h3>
              <p className="text-gray-400 text-sm">
                Visual breakdown of your health across 10+ categories with actionable insights.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
            >
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <Leaf className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Supplement Plan</h3>
              <p className="text-gray-400 text-sm">
                Personalized supplement recommendations with dosages, timing, and what to look for.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
            >
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Safety Analysis</h3>
              <p className="text-gray-400 text-sm">
                Complete drug interaction check with warnings and safe alternatives.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sample Report Modal */}
      <AnimatePresence>
        {showSampleReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSampleReport(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-xl font-bold text-[#1A1A1A]">Sample Assessment Report</h3>
                  <p className="text-sm text-muted-foreground">Preview of what you'll receive</p>
                </div>
                <button
                  onClick={() => setShowSampleReport(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Content */}
              <div className="p-6">
                {/* Health Scores Preview */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-primary" />
                    Health Assessment Scores
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { name: 'Energy', score: 65, color: 'bg-amber-500' },
                      { name: 'Sleep', score: 45, color: 'bg-indigo-500' },
                      { name: 'Immunity', score: 78, color: 'bg-green-500' },
                      { name: 'Stress', score: 55, color: 'bg-red-500' },
                    ].map((item) => (
                      <div key={item.name} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium">{item.name}</span>
                          <span className="text-sm font-bold">{item.score}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.score}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Supplement Recommendations Preview */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-green-600" />
                    Personalized Supplement Recommendations
                  </h4>
                  <div className="space-y-3">
                    {[
                      { name: 'Vitamin D3', dosage: '2000-4000 IU', timing: 'Morning with food', priority: 'Essential' },
                      { name: 'Magnesium Glycinate', dosage: '300-400mg', timing: 'Evening', priority: 'High' },
                      { name: 'Omega-3 Fish Oil', dosage: '1000-2000mg', timing: 'With meals', priority: 'High' },
                    ].map((supp, i) => (
                      <div key={supp.name} className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-100">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                            {i + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-[#1A1A1A]">{supp.name}</p>
                            <p className="text-sm text-muted-foreground">{supp.dosage} • {supp.timing}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          supp.priority === 'Essential' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {supp.priority}
                        </span>
                      </div>
                    ))}
                    
                    {/* Blurred items to show there's more */}
                    <div className="relative">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 blur-sm">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gray-100"></div>
                          <div>
                            <p className="font-semibold">Ashwagandha KSM-66</p>
                            <p className="text-sm text-muted-foreground">300mg • Morning</p>
                          </div>
                        </div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                          <Lock className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">+5 more recommendations</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Drug Interactions Preview */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Drug Interaction Analysis
                  </h4>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-amber-800">
                      <strong>Your medications are checked against all recommendations.</strong> Any potential 
                      interactions are flagged with clear explanations and safer alternatives when available.
                    </p>
                  </div>
                </div>
                
                {/* CTA */}
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 text-center">
                  <h4 className="text-xl font-bold text-[#1A1A1A] mb-2">Ready for Your Personalized Report?</h4>
                  <p className="text-muted-foreground mb-6">Takes only 5 minutes. 100% free.</p>
                  <Button 
                    onClick={() => {
                      setShowSampleReport(false);
                      handleStartQuiz();
                    }}
                    size="lg"
                    className="btn-primary text-lg px-8"
                  >
                    Start My Free Assessment
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Who's Behind the AI - Credibility Section */}
      <section className="py-10 bg-white border-t border-b border-border/30">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
              <Brain className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4">
              The Science Behind Our AI
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-6">
              Our AI recommendations are based on peer-reviewed research and established supplement guidelines. 
              We encourage users to consult with their healthcare provider before starting any supplement regimen.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F0] rounded-full">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Peer-Reviewed Research</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F0] rounded-full">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Evidence-Based Guidelines</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F0] rounded-full">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Continuously Updated</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-sm uppercase tracking-widest text-primary font-medium">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A] mt-4">
              Three Simple Steps to Better Health
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
                className="relative p-8 rounded-2xl bg-white border border-border/50 card-hover"
              >
                <span className="text-6xl font-bold text-primary/10 absolute top-6 right-6">
                  {feature.icon}
                </span>
                <h3 className="text-xl font-medium text-[#1A1A1A] mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Only shows if there are approved testimonials */}
      {testimonials.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="text-sm uppercase tracking-widest text-primary font-medium">Testimonials</span>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A] mt-4">
                What Our Users Say
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((testimonial, i) => (
                <motion.div
                  key={testimonial.id || testimonial._id || i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  viewport={{ once: true }}
                  className="p-8 rounded-2xl bg-[#F5F5F0] border border-border/30"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-[#1A1A1A] mb-6 leading-relaxed">"{testimonial.text || testimonial.content}"</p>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{testimonial.name || 'Vitala User'}</p>
                    {testimonial.role && <p className="text-sm text-muted-foreground">{testimonial.role}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 bg-primary">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to Transform Your Health?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Take our free 5-minute assessment and discover the perfect supplements for your unique health profile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={handleStartQuiz}
                className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-3 text-base font-medium hover:scale-105 transition-all duration-300"
              >
                Start Your Free Assessment
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
              {user && (
                <Button 
                  variant="outline"
                  onClick={() => setShowTestimonialModal(true)}
                  className="rounded-full px-6 border-white/30 text-white hover:bg-white/10 hover:text-white"
                  data-testid="share-story-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Share Your Story
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-10 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {subscribed ? (
              <div className="py-6">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-2">You're Subscribed!</h3>
                <p className="text-muted-foreground">Check your inbox for a welcome email with health tips.</p>
              </div>
            ) : (
              <>
                <Mail className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-3">
                  Free Weekly Health Tips
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  Join our community getting evidence-based supplement insights and wellness tips every week.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    className="flex-1 bg-white"
                    data-testid="landing-newsletter-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={subscribing}
                    className="btn-primary whitespace-nowrap"
                    data-testid="landing-newsletter-button"
                  >
                    {subscribing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Subscribe
                      </>
                    )}
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground mt-3">No spam. Unsubscribe anytime.</p>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo192.png" alt="Vitala" className="w-6 h-6 object-contain" />
                <span className="text-xl font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Vitala</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                Personalized supplement recommendations powered by AI, with medication interaction safety checks.
              </p>
              <div className="mt-5">
                <AppStoreBadges variant="both" size="sm" align="start" />
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="flex flex-col gap-3 text-sm text-white/60">
                <Link to="/quiz" className="hover:text-white transition-colors">Take Assessment</Link>
                <Link to="/supplements" className="hover:text-white transition-colors">Browse Supplements</Link>
                <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
                <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
                <Link to="/community" className="hover:text-white transition-colors">Blog & Resources</Link>
                <Link to="/app" className="hover:text-white transition-colors">Get the App</Link>
              </div>
            </div>
            
            {/* Account */}
            <div>
              <h4 className="font-semibold mb-4">Account</h4>
              <div className="flex flex-col gap-3 text-sm text-white/60">
                <Link to="/auth?mode=login" className="hover:text-white transition-colors">Sign In</Link>
                <Link to="/auth?mode=register" className="hover:text-white transition-colors">Create Account</Link>
                <Link to="/provider" className="hover:text-white transition-colors">Provider Portal</Link>
                <Link to="/contact" className="hover:text-white transition-colors">Contact Support</Link>
              </div>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <div className="flex flex-col gap-3 text-sm text-white/60">
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
                <Link to="/disclaimer" className="hover:text-white transition-colors">Medical Disclaimer</Link>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} Medpax, LLC. Vitala™ is a trademark of Medpax, LLC. All rights reserved.
            </p>
            <p className="text-white/40 text-xs max-w-xl text-center md:text-right">
              Disclaimer: Vitala provides educational information only and is not a substitute for professional medical advice. 
              Always consult your healthcare provider before starting any supplement regimen.
            </p>
          </div>
        </div>
      </footer>

      {/* Testimonial Modal */}
      <TestimonialModal 
        isOpen={showTestimonialModal} 
        onClose={() => setShowTestimonialModal(false)} 
      />
    </div>
  );
}
